import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request Interceptor: Token anhängen
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response Interceptor: 401 → Logout
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============ AUTH ============
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/auth/change-password', { currentPassword, newPassword }),
};

// ============ TREES ============
export const treesAPI = {
  getAll: (params?: Record<string, any>) =>
    api.get('/trees', { params }),
  getById: (id: string) =>
    api.get(`/trees/${id}`),
  getStats: () =>
    api.get('/trees/stats'),
  getFilterOptions: () =>
    api.get('/trees/filter-options'),
  getMap: (params?: Record<string, any>) =>
    api.get('/trees/map', { params }),
  create: (data: any) =>
    api.post('/trees', data),
  update: (id: string, data: any) =>
    api.put(`/trees/${id}`, data),
  delete: (id: string) =>
    api.delete(`/trees/${id}`),
};

// ============ INSPECTIONS ============
export const inspectionsAPI = {
  getByTree: (treeId: string) =>
    api.get(`/inspections/tree/${treeId}`),
  create: (data: any) =>
    api.post('/inspections', data),
  delete: (id: string) =>
    api.delete(`/inspections/${id}`),
};

// ============ MEASURES ============
export const measuresAPI = {
  getAll: (params?: Record<string, any>) =>
    api.get('/measures', { params }),
  create: (data: any) =>
    api.post('/measures', data),
  update: (id: string, data: any) =>
    api.put(`/measures/${id}`, data),
  delete: (id: string) =>
    api.delete(`/measures/${id}`),
};

// ============ DAMAGES ============
export const damagesAPI = {
  getAll: (params?: Record<string, any>) =>
    api.get('/damages', { params }),
  create: (data: any) =>
    api.post('/damages', data),
  update: (id: string, data: any) =>
    api.put(`/damages/${id}`, data),
  delete: (id: string) =>
    api.delete(`/damages/${id}`),
};

// ============ PHOTOS ============
export const photosAPI = {
  getByTree: (treeId: string) =>
    api.get(`/photos/tree/${treeId}`),
  upload: (treeId: string, file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('tree_id', treeId);
    return api.post('/photos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id: string) =>
    api.delete(`/photos/${id}`),
};

// ============ USERS ============
export const usersAPI = {
  getAll: (params?: Record<string, any>) =>
    api.get('/users', { params }),
  getById: (id: string) =>
    api.get(`/users/${id}`),
  create: (data: any) =>
    api.post('/users', data),
  update: (id: string, data: any) =>
    api.put(`/users/${id}`, data),
  delete: (id: string) =>
    api.delete(`/users/${id}`),
  toggleActive: (id: string) =>
    api.put(`/users/${id}/toggle-active`),
};

// ============ IMPORT ============
export const importAPI = {
  getTemplate: () =>
    api.get('/import/template', { responseType: 'blob' }),
  preview: (file: File, delimiter?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (delimiter) formData.append('delimiter', delimiter);
    return api.post('/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  execute: (file: File, options?: { delimiter?: string; update_existing?: boolean; column_mapping?: any }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.delimiter) formData.append('delimiter', options.delimiter);
    if (options?.update_existing) formData.append('update_existing', 'true');
    if (options?.column_mapping) formData.append('column_mapping', JSON.stringify(options.column_mapping));
    return api.post('/import/execute', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getHistory: () =>
    api.get('/import/history'),
};

// ============ REPORTS ============
export const reportsAPI = {
  getDashboard: () =>
    api.get('/reports/dashboard'),
  getTreesByCondition: () =>
    api.get('/reports/trees-by-condition'),
  getTreesBySpecies: () =>
    api.get('/reports/trees-by-species'),
  getTreesByLocation: () =>
    api.get('/reports/trees-by-location'),
  exportCSV: () =>
    api.get('/reports/export/csv', { responseType: 'blob' }),
  getAuditLog: (params?: Record<string, any>) =>
    api.get('/reports/audit-log', { params }),
};

// ============ HEALTH ============
export const healthAPI = {
  check: () => api.get('/health'),
};

export default api;
